/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Modal, Table, Checkbox, App, Tooltip } from 'antd'
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { RegisteredPlayer, Team } from '@sportninja/common/types/Registration'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Text from 'src/components/ts-components/Text'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Button from 'src/components/ts-components/Button'
import {
  getErrorMessage,
  getImageThumbnailId,
} from '@sportninja/common/utils/utils'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import req from '@sportninja/common/api/request'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'

interface ManagePlayersModalProps {
  isOpen: boolean
  onClose: () => void
  selectedPlayers: RegisteredPlayer[]
  onClearSelection: () => void
  allTeams: (Team & { division_name: string; division_id: string })[]
  scheduleId: string
  onSuccess?: () => void
}

const styles = css`
  .my-modal-body {
    max-height: calc(90vh - 110px);
    overflow-y: auto;
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
    position: sticky;
    bottom: 0;
    background: ${colors.SECONDARY_300};
    padding: 16px;
    border-top: 1px solid ${colors.SOFT_STEEL};
  }
  .my-modal-content {
    border: 1px solid #333;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    flex: 1,
    overflow: 'auto',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: colors.SECONDARY_300,
  },
}

const TeamOption = ({
  team,
  isBucket = false,
}: {
  team: Team
  isBucket?: boolean
}) => (
  <div
    css={css`
      display: flex;
      align-items: center;
      gap: 8px;
    `}
  >
    <img
      alt={team.name}
      css={css`
        width: 24px;
        height: 24px;
        border-radius: 4px;
        object-fit: contain;
      `}
      onError={(e) => {
        const img = e.target as HTMLImageElement
        img.onerror = null
        img.src = '/images/generic_placeholders/placeholder_team.png'
      }}
      src={
        isBucket
          ? '/images/bucket_image.png'
          : !getImageThumbnailId(team.image)
          ? '/images/generic_placeholders/placeholder_team.png'
          : getImageThumbnailId(team.image)
      }
    />
    <Text variant='paragraphSmall' weight='regular'>
      {team.name}
    </Text>
  </div>
)

const renderTeamSelectOptions = (allTeams: Team[], originTeamId: string) => {
  const buckets = allTeams?.filter((team) => team.is_bucket) || []
  const teams = allTeams?.filter((team) => !team.is_bucket) || []

  const options = []

  // Only show buckets if they exist
  if (buckets.length > 0) {
    // @ts-ignore
    options.push({
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.PRIMARY}
            style='text-transform: uppercase;'
          >
            Buckets
          </Text>
          <Tooltip
            title="A bucket is a temporary team used to group players who don't have a team yet. Players in buckets will be drafted or assigned to teams later."
            zIndex={12000}
          >
            <InfoCircleOutlined
              css={css`
                color: ${colors.PRIMARY};
                font-size: 16px;
                margin-left: 8px;
              `}
            />
          </Tooltip>
        </div>
      ),
      title: 'buckets',
      options: buckets
        .filter(
          (team) => team.registration_status !== 5 && team.id !== originTeamId
        )
        .sort((a, b) => a?.name?.localeCompare(b?.name || ''))
        .map((team) => ({
          label: <TeamOption team={team} isBucket={true} />,
          value: team.id,
        })),
    })
  }
  // Show teams section
  // @ts-ignore
  options.push({
    label: (
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        `}
      >
        <Text
          variant='paragraphSmall'
          weight='semiBold'
          color={colors.PRIMARY}
          style='text-transform: uppercase;'
        >
          Teams
        </Text>
      </div>
    ),
    title: 'teams',
    options: teams
      .filter(
        (team) => team.registration_status !== 5 && team.id !== originTeamId
      )
      .sort((a, b) => a?.name?.localeCompare(b?.name || ''))
      .map((team) => ({
        label: <TeamOption team={team} />,
        value: team.id,
      })),
  })

  return options
}

const movePlayers = async ({
  scheduleId,
  originTeamPlayerRosterId,
  players,
  destinationTeamRosterId,
  destinationScheduleId,
  notifyPlayer,
  notificationMessage,
}: {
  scheduleId: string
  originTeamPlayerRosterId: string
  players: RegisteredPlayer[]
  destinationTeamRosterId: string
  destinationScheduleId: string
  notifyPlayer: boolean
  notificationMessage: string
}) => {
  if (!originTeamPlayerRosterId) {
    throw new Error('Invalid origin team player roster ID')
  }
  if (!destinationTeamRosterId) {
    throw new Error('Invalid destination team roster ID')
  }
  if (!scheduleId) {
    throw new Error('Invalid schedule ID')
  }
  if (!destinationScheduleId) {
    throw new Error('Invalid destination schedule ID')
  }
  if (!players) {
    throw new Error('Invalid players')
  }
  const response = await req(
    `/registration/${scheduleId}/admin/trade-players/${originTeamPlayerRosterId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        players: players.map((player) => ({
          id: player.player_id,
        })),
        destination_team_player_roster_id: destinationTeamRosterId,
        destination_schedule_id: destinationScheduleId,
        send_notification: notifyPlayer,
        notification_message: notificationMessage,
      }),
    }
  )
  return response
}

const ManagePlayersModal: React.FC<ManagePlayersModalProps> = ({
  isOpen,
  onClose,
  selectedPlayers,
  onClearSelection,
  allTeams,
  scheduleId,
  onSuccess,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const [loading, setLoading] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState('')
  const [notifyPlayer, setNotifyPlayer] = useState(true)
  const [notificationMessage, setNotificationMessage] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const { openModal, closeModal } = useModalScroll()
  const { message } = App.useApp()

  useEffect(() => {
    if (isOpen) {
      openModal()
      // Initialize selected rows with current selected players
      setSelectedRowKeys(selectedPlayers.map((player) => player?.id))
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal, selectedPlayers])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const handleCancel = () => {
    setSelectedTeam('')
    setNotifyPlayer(true)
    onClearSelection()
    onClose()
  }

  const handleMove = async () => {
    try {
      setLoading(true)
      const team = allTeams.find((t) => t.id === selectedTeam)
      // Filter selectedPlayers based on selectedRowKeys
      const playersToMove = selectedPlayers.filter((player) =>
        selectedRowKeys.includes(player.id)
      )
      await movePlayers({
        scheduleId,
        originTeamPlayerRosterId: playersToMove[0].team_roster_id,
        players: playersToMove,
        destinationTeamRosterId: team?.team_roster_id || '',
        destinationScheduleId: team?.division_id || '',
        notifyPlayer,
        notificationMessage: 'You have been traded to a new team!',
      })
      message.success('Players Traded Successfully')
      onClose()
      onSuccess?.()
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      message.error({
        content: errorMessage,
        duration: 5,
      })
    } finally {
      setLoading(false)
    }
  }

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
        `}
      >
        <Button
          text='Cancel'
          onClick={handleCancel}
          variant='secondary'
          disabled={loading}
        />
        <Button
          text='Trade'
          onClick={handleMove}
          variant='primary'
          disabled={loading || !selectedTeam || selectedRowKeys?.length === 0}
        />
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedTeam, selectedRowKeys])

  const selectedPlayersColumns = [
    {
      title: '',
      dataIndex: 'id',
      key: 'selection',
      width: '48px',
      render: (_: any, record: RegisteredPlayer) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            const newSelectedKeys = e.target.checked
              ? [...selectedRowKeys, record.id]
              : selectedRowKeys.filter((key) => key !== record.id)
            setSelectedRowKeys(newSelectedKeys)
          }}
          css={css`
            .ant-checkbox-inner {
              background-color: ${colors.SECONDARY_200};
              border-color: ${colors.WHITE};
            }
            &:hover .ant-checkbox-inner {
              border-color: ${colors.PRIMARY};
            }
            .ant-checkbox-checked .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }
          `}
        />
      ),
    },
    {
      title: 'NAME',
      dataIndex: 'name_full',
      key: 'name_full',
      width: '50%',
      render: (name: string, record: any) => (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <img
            alt={name}
            css={css`
              width: 24px;
              height: 24px;
              border-radius: 4px;
              object-fit: contain;
            `}
            onError={(e) => {
              const img = e.target as HTMLImageElement
              img.onerror = null
              img.src = '/images/generic_placeholders/placeholder_player.png'
            }}
            src={
              !getImageThumbnailId(record)
                ? '/images/generic_placeholders/placeholder_player.png'
                : getImageThumbnailId(record)
            }
          />
          <Text variant='paragraphSmall' weight='regular'>
            {name}
          </Text>
        </div>
      ),
    },
    {
      title: 'TEAM',
      dataIndex: 'team_id',
      key: 'team_id',
      width: '50%',
      render: (team_id: string) => {
        const team = allTeams.find((t) => t.id === team_id)
        if (!team) {
          return '-'
        }
        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <img
              alt={team.name}
              css={css`
                width: 24px;
                height: 24px;
                border-radius: 4px;
                object-fit: contain;
              `}
              onError={(e) => {
                const img = e.target as HTMLImageElement
                img.onerror = null
                img.src = '/images/generic_placeholders/placeholder_team.png'
              }}
              src={
                !getImageThumbnailId(team.image)
                  ? '/images/generic_placeholders/placeholder_team.png'
                  : getImageThumbnailId(team.image)
              }
            />
            <Text variant='paragraphSmall' weight='regular'>
              {team.name}
            </Text>
          </div>
        )
      },
    },
  ]

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      width={1080}
      closeIcon={closeIcon()}
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Trade Players
        </Text>
      }
      open={isOpen}
      onCancel={handleCancel}
      footer={getFooter()}
      centered
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
      css={css`
        .ant-modal-content {
          overflow: hidden;
          height: ${isMobile ? '100%' : '60vh'} !important;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          border-bottom: none !important;
        }
        .ant-modal-footer {
          position: sticky;
          bottom: 0;
          background: ${colors.SECONDARY_300};
          padding: 16px;
          border-top: 1px solid ${colors.SOFT_STEEL};
        }
        height: ${isMobile ? '100%' : '60vh'} !important;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 32px;
          padding: 24px;
          height: 100%;
          ${isMobile && 'flex-direction: column;'}
        `}
      >
        {/* Selected Players Section */}
        <div
          css={css`
            flex: 1;
          `}
        >
          <Text
            variant='captionLarge'
            weight='semiBold'
            style={`
              margin-bottom: 24px;
              display: block;
              padding-bottom: 8px;
              border-bottom: 1px solid ${colors.SOFT_STEEL};
            `}
          >
            Selected Players
          </Text>
          <Table
            dataSource={selectedPlayers}
            columns={selectedPlayersColumns}
            pagination={false}
            size='small'
            bordered
            scroll={{ x: isMobile ? 500 : 'max-content' }}
            css={css`
              .ant-table {
                border: 1px solid ${colors.SECONDARY_400} !important;
              }
              .ant-table-cell {
                border-color: ${colors.SECONDARY_400} !important;
              }
              .ant-table-thead > tr > th {
                padding: 4px !important;
                background-color: ${colors.SECONDARY_400} !important;
                text-transform: uppercase;
                font-weight: 600;
              }
              .ant-table-tbody > tr:nth-child(even) {
                background-color: ${colors.SECONDARY_300};
              }
              .ant-table-tbody > tr:nth-child(odd) {
                background-color: ${colors.SECONDARY_400};
              }
              .ant-table-tbody > tr > td {
                vertical-align: middle;
              }
              @media (max-width: 768px) {
                /* padding: 0 16px !important; */
              }
            `}
          />
        </div>

        {/* Separator */}
        <div
          css={css`
            width: 1px;
            background-color: ${colors.SOFT_STEEL};
            margin: 0 8px;
          `}
        />

        {/* Select Team Section */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <Text
            variant='captionLarge'
            weight='semiBold'
            style={`
              display: block;
              padding-bottom: 8px;
              border-bottom: 1px solid ${colors.SOFT_STEEL};
            `}
          >
            Select Destination Team
          </Text>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                width: 100%;
              `}
            >
              <SelectInput
                name='team'
                label='Team *'
                options={renderTeamSelectOptions(
                  allTeams,
                  selectedPlayers[0].team_id || ''
                )}
                onChange={(value) => setSelectedTeam(value)}
                value={selectedTeam}
                fieldErrors={[]}
                customError={{}}
                disabled={loading}
                placeholder='Select a Team'
                showSearch
                filterOption={(input, option) => {
                  return (option?.label?.props?.team?.name || '')
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }}
              />
            </div>
            <Checkbox
              checked={notifyPlayer}
              onChange={(e) => setNotifyPlayer(e.target.checked)}
              css={css`
                .ant-checkbox-inner {
                  background-color: ${colors.SECONDARY_200};
                  border-color: ${colors.WHITE};
                }
                .ant-checkbox-wrapper {
                  color: ${colors.WHITE};
                }
                &:hover .ant-checkbox-inner {
                  border-color: ${colors.PRIMARY};
                }
                .ant-checkbox-checked .ant-checkbox-inner {
                  background-color: ${colors.PRIMARY};
                  border-color: ${colors.PRIMARY};
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                Notify player about the trade
              </Text>
            </Checkbox>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                width: 100%;
              `}
            >
              <TextAreaInput
                name='notification_message'
                label='Notification Message'
                value={notificationMessage}
                setValue={(value) => setNotificationMessage(value)}
                fieldErrors={[]}
                customError={{}}
                disabled={loading}
                placeholder='Enter a message to notify the player about the trade'
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ManagePlayersModal
